import React from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { FiArrowRight, FiMapPin, FiCamera, FiHeart } from 'react-icons/fi';
import Button from '../../components/UI/Button';

const HomeContainer = styled.div`
  min-height: 100vh;
`;

const HeroSection = styled.section`
  height: 90vh;
  background: linear-gradient(
    rgba(0, 0, 0, 0.4),
    rgba(0, 0, 0, 0.4)
  ), url('/images/yosemite.jpg');
  background-size: cover;
  background-position: center;
  background-attachment: fixed;
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;
  color: white;
  position: relative;
  
  @media (max-width: 768px) {
    height: 80vh;
    background-attachment: scroll;
  }
`;

const HeroContent = styled(motion.div)`
  max-width: 800px;
  padding: 0 var(--space-lg);
  z-index: 2;
`;

const HeroTitle = styled.h1`
  font-size: 3rem;
  font-weight: 700;
  margin-bottom: var(--space-lg);
  text-shadow: 2px 2px 4px rgba(0, 0, 0, 0.5);
  
  @media (max-width: 768px) {
    font-size: 2.5rem;
  }
  
  @media (max-width: 480px) {
    font-size: 2rem;
  }
`;

const HeroSubtitle = styled.p`
  font-size: 1.25rem;
  margin-bottom: var(--space-2xl);
  line-height: 1.6;
  text-shadow: 1px 1px 2px rgba(0, 0, 0, 0.5);
  
  @media (max-width: 768px) {
    font-size: 1.125rem;
  }
`;

const HeroButtons = styled.div`
  display: flex;
  gap: var(--space-lg);
  justify-content: center;
  flex-wrap: wrap;
  
  @media (max-width: 480px) {
    flex-direction: column;
    align-items: center;
  }
`;

const Section = styled.section`
  padding: var(--space-3xl) 0;
  
  @media (max-width: 768px) {
    padding: var(--space-2xl) 0;
  }
`;

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 var(--space-lg);
`;

const SectionHeader = styled.div`
  text-align: center;
  margin-bottom: var(--space-3xl);
  
  h2 {
    font-size: 2.5rem;
    color: var(--neutral-gray-900);
    margin-bottom: var(--space-md);
    
    @media (max-width: 768px) {
      font-size: 2rem;
    }
  }
  
  p {
    font-size: 1.125rem;
    color: var(--neutral-gray-600);
    max-width: 600px;
    margin: 0 auto;
  }
`;

const AboutGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: var(--space-2xl);
  align-items: center;
  
  @media (min-width: 768px) {
    grid-template-columns: 1fr 1fr;
  }
`;

const AboutContent = styled(motion.div)`
  h3 {
    font-size: 1.875rem;
    color: var(--neutral-gray-900);
    margin-bottom: var(--space-lg);
  }
  
  p {
    font-size: 1.125rem;
    line-height: 1.7;
    color: var(--neutral-gray-600);
    margin-bottom: var(--space-lg);
  }
`;

const AboutImage = styled(motion.div)`
  background: url('/images/harvesthosts.jpg');
  background-size: cover;
  background-position: center;
  height: 400px;
  border-radius: var(--radius-2xl);
  box-shadow: var(--shadow-xl);
  
  @media (max-width: 768px) {
    height: 300px;
  }
`;

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: var(--space-xl);
  margin-top: var(--space-2xl);
`;

const StatCard = styled(motion.div)`
  text-align: center;
  padding: var(--space-xl);
  background: white;
  border-radius: var(--radius-xl);
  box-shadow: var(--shadow-md);
  
  .icon {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 60px;
    height: 60px;
    background: linear-gradient(135deg, var(--primary-green), var(--primary-green-light));
    border-radius: 50%;
    margin-bottom: var(--space-lg);
    color: white;
  }
  
  .number {
    font-size: 2rem;
    font-weight: 700;
    color: var(--neutral-gray-900);
    margin-bottom: var(--space-sm);
  }
  
  .label {
    color: var(--neutral-gray-600);
    font-weight: 500;
  }
`;

const FeaturesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: var(--space-2xl);
`;

const FeatureCard = styled(motion.div)`
  text-align: center;
  padding: var(--space-2xl);
  background: white;
  border-radius: var(--radius-2xl);
  box-shadow: var(--shadow-lg);
  transition: var(--transition-base);
  
  &:hover {
    transform: translateY(-4px);
    box-shadow: var(--shadow-xl);
  }
  
  .icon {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 80px;
    height: 80px;
    background: linear-gradient(135deg, var(--primary-green), var(--secondary-blue));
    border-radius: 50%;
    margin-bottom: var(--space-lg);
    color: white;
  }
  
  h3 {
    font-size: 1.5rem;
    color: var(--neutral-gray-900);
    margin-bottom: var(--space-md);
  }
  
  p {
    color: var(--neutral-gray-600);
    line-height: 1.6;
    margin-bottom: var(--space-lg);
  }
`;

const CtaSection = styled.section`
  background: linear-gradient(135deg, var(--primary-green), var(--secondary-blue));
  color: white;
  text-align: center;
  padding: var(--space-3xl) 0;
  
  h2 {
    font-size: 2.5rem;
    margin-bottom: var(--space-lg);
    
    @media (max-width: 768px) {
      font-size: 2rem;
    }
  }
  
  p {
    font-size: 1.25rem;
    margin-bottom: var(--space-2xl);
    max-width: 600px;
    margin-left: auto;
    margin-right: auto;
  }
`;

const Home = () => {
  const stats = [
    { icon: <FiMapPin size={24} />, number: "50+", label: "States Visited" },
    { icon: <FiCamera size={24} />, number: "1000+", label: "Photos Shared" },
    { icon: <FiHeart size={24} />, number: "7+", label: "Years RVing" },
    { icon: <FiArrowRight size={24} />, number: "2", label: "Years Full-Time" }
  ];

  const features = [
    {
      icon: <FiMapPin size={32} />,
      title: "State & National Parks",
      description: "Discover amazing destinations across America with detailed guides, reviews, and stunning photography from our travels.",
      link: "/parks"
    },
    {
      icon: <FiCamera size={32} />,
      title: "Photo Gallery",
      description: "Browse through thousands of beautiful photos from our adventures, including landscapes, wildlife, and RV life moments.",
      link: "/gallery"
    },
    {
      icon: <FiHeart size={32} />,
      title: "RV Life Blog",
      description: "Read about our experiences, tips for full-time RV living, gear reviews, and practical advice for your own adventures.",
      link: "/blog"
    }
  ];

  return (
    <HomeContainer>
      {/* Hero Section */}
      <HeroSection>
        <HeroContent
          initial={{ opacity: 0, y: 50 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
        >
          <HeroTitle>Adventure Awaits on the Open Road</HeroTitle>
          <HeroSubtitle>
            Join Luke, Patty, and Dobby as they explore America's most beautiful 
            destinations in their home on wheels. Full-time RV living since 2023!
          </HeroSubtitle>
          <HeroButtons>
            <Button as={Link} to="/about" size="lg">
              Our Story
            </Button>
            <Button as={Link} to="/parks" variant="secondary" size="lg">
              Explore Parks
            </Button>
          </HeroButtons>
        </HeroContent>
      </HeroSection>

      {/* About Section */}
      <Section>
        <Container>
          <AboutGrid>
            <AboutContent
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
            >
              <h3>Living Life Better Together</h3>
              <p>
                We're Luke and Patty, and together with our beloved dog Dobby, 
                we've been exploring America's incredible landscapes since 2018. 
                After making the leap to full-time RV living in 2023, we've 
                discovered that life truly is better when shared.
              </p>
              <p>
                Our mission is to inspire others to explore the great outdoors, 
                discover hidden gems, and create lasting memories on the road. 
                Whether you're a seasoned RVer or just dreaming of the nomadic 
                lifestyle, we're here to share our journey with you.
              </p>
              <Button as={Link} to="/about">
                Learn More About Us
                <FiArrowRight size={18} />
              </Button>
            </AboutContent>
            
            <AboutImage
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
            />
          </AboutGrid>

          <StatsGrid>
            {stats.map((stat, index) => (
              <StatCard
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                viewport={{ once: true }}
              >
                <div className="icon">{stat.icon}</div>
                <div className="number">{stat.number}</div>
                <div className="label">{stat.label}</div>
              </StatCard>
            ))}
          </StatsGrid>
        </Container>
      </Section>

      {/* Features Section */}
      <Section style={{ backgroundColor: 'var(--neutral-gray-50)' }}>
        <Container>
          <SectionHeader>
            <h2>Explore Our Adventures</h2>
            <p>
              Discover beautiful destinations, read engaging stories, and get 
              inspired for your own RV adventures.
            </p>
          </SectionHeader>

          <FeaturesGrid>
            {features.map((feature, index) => (
              <FeatureCard
                key={index}
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.2 }}
                viewport={{ once: true }}
              >
                <div className="icon">{feature.icon}</div>
                <h3>{feature.title}</h3>
                <p>{feature.description}</p>
                <Button as={Link} to={feature.link} variant="outline">
                  Explore
                  <FiArrowRight size={16} />
                </Button>
              </FeatureCard>
            ))}
          </FeaturesGrid>
        </Container>
      </Section>

      {/* CTA Section */}
      <CtaSection>
        <Container>
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            <h2>Ready to Start Your Adventure?</h2>
            <p>
              Join our community of RV enthusiasts and get access to exclusive 
              content, detailed guides, and insider tips for your next journey.
            </p>
            <Button as={Link} to="/register" variant="secondary" size="lg">
              Join Our Community
              <FiArrowRight size={20} />
            </Button>
          </motion.div>
        </Container>
      </CtaSection>
    </HomeContainer>
  );
};

export default Home;
